/**
 * Telemetry wrapper for the web app: product events (Umami) and error tracking
 * (GlitchTip) through the shared `ambient-telemetry` library. Call sites import
 * from here, not from `ambient-telemetry` directly, so the config and the
 * no-throw guarantees live in one place.
 *
 * Separate from apps/web/src/landing/* — that pipeline (consent-gated Meta
 * Pixel / Google Ads / internal tracker) tracks anonymous marketing visitors.
 * Landing code only reuses `trackTelemetryEvent` / `recordPage` below.
 *
 * Only active in production builds with at least one backend configured
 * (VITE_UMAMI_HOST + VITE_UMAMI_WEBSITE_ID, VITE_GLITCHTIP_DSN), so dev, test
 * and unconfigured builds never send anything. The library and the Sentry SDK
 * load lazily; calls made before they arrive are queued.
 */

import { matchPath } from 'react-router-dom';
import type { TelemetryConfig } from 'ambient-telemetry/web';
import type { ProductAnalyticsEventName } from '@tilly/shared';

export interface TelemetryEnv {
  PROD?: boolean;
  VITE_UMAMI_HOST?: string;
  VITE_UMAMI_WEBSITE_ID?: string;
  VITE_GLITCHTIP_DSN?: string;
  VITE_APP_VERSION?: string;
}

type Telemetry = typeof import('ambient-telemetry/web');
type Call = (t: Telemetry) => void;

/** Query params worth keeping for traffic attribution; everything else (OAuth
 * `code`/`state`, invite and reset tokens, ...) is dropped. */
const KEPT_PARAMS = /^(utm_[a-z]+|gclid|gbraid|wbraid)$/i;

/**
 * Every route the app serves (see App.tsx / MarketingApp.tsx), most specific
 * first. A `:param` in a pattern is reported as its name (`/admin/clients/:id`)
 * so record ids and secrets (`/join/:token`, `/a/:appointmentId`, `/c/:clientId`)
 * never reach Umami or GlitchTip. Params listed in `keep` hold public, curated
 * values (a booking slug, a tool or SEO page slug) and are reported as-is.
 */
const ROUTES: readonly { pattern: string; keep?: readonly string[] }[] = [
  { pattern: '/' },
  { pattern: '/about' },
  { pattern: '/privacy' },
  { pattern: '/support' },
  { pattern: '/callback' },
  { pattern: '/bridge' },
  { pattern: '/join/:token' },
  { pattern: '/book/:slug', keep: ['slug'] },
  { pattern: '/book/:slug/manage', keep: ['slug'] },
  { pattern: '/a/:appointmentId' },
  { pattern: '/c/:clientId' },
  { pattern: '/lp/:experimentName', keep: ['experimentName'] },
  { pattern: '/tools' },
  { pattern: '/tools/:toolSlug', keep: ['toolSlug'] },
  { pattern: '/admin/setup' },
  { pattern: '/admin/setup/business' },
  { pattern: '/admin/setup/provider' },
  { pattern: '/admin/dashboard' },
  { pattern: '/admin/calendar' },
  { pattern: '/admin/my-appointments' },
  { pattern: '/admin/clients' },
  { pattern: '/admin/clients/:id' },
  { pattern: '/admin/messages' },
  { pattern: '/admin/messages/:clientId' },
  { pattern: '/admin/suggestions' },
  { pattern: '/admin/providers' },
  { pattern: '/admin/providers/:providerId/performance' },
  { pattern: '/admin/profile' },
  { pattern: '/admin/schedule' },
  { pattern: '/admin/settings' },
  { pattern: '/admin/booking-page' },
  { pattern: '/admin/catalog' },
  { pattern: '/admin/reports' },
  { pattern: '/admin/styleguide' },
  // Single-segment SEO landing pages; must come after every fixed one-segment route.
  { pattern: '/:seoSlug', keep: ['seoSlug'] },
];

/** A curated public slug: short, lowercase, no room for a token or id to hide in. */
const PUBLIC_SLUG = /^[a-z0-9][a-z0-9-]{0,63}$/;

/** Maps a pathname to its route pattern. Unknown paths collapse to `/*` (fails closed). */
export function normalizePath(pathname: string): string {
  for (const { pattern, keep } of ROUTES) {
    const match = matchPath({ path: pattern, end: true }, pathname);
    if (!match) continue;
    return pattern.replace(/:([A-Za-z]+)/g, (whole, name: string) => {
      const value = match.params[name];
      return keep?.includes(name) && value && PUBLIC_SLUG.test(value) ? value : whole;
    });
  }
  return '/*';
}

/** Applied by the library to every URL it reports (Umami events and pageviews,
 * Sentry request URL, referer and breadcrumbs): route ids/tokens collapse to
 * the route pattern and only attribution query params survive. */
export function sanitizeUrl(url: string): string {
  const absolute = /^[a-z][a-z0-9+.-]*:/i.test(url);
  try {
    const parsed = new URL(url, 'http://placeholder.invalid');
    const kept = new URLSearchParams();
    parsed.searchParams.forEach((value, key) => {
      if (KEPT_PARAMS.test(key)) kept.append(key, value);
    });
    const query = kept.toString();
    return (
      (absolute ? parsed.origin : '') + normalizePath(parsed.pathname) + (query ? `?${query}` : '')
    );
  } catch {
    return '/';
  }
}

/** Null unless this is a production build with at least one backend configured. */
export function telemetryConfig(env: TelemetryEnv): TelemetryConfig | null {
  if (!env.PROD) return null;
  const umami =
    env.VITE_UMAMI_HOST && env.VITE_UMAMI_WEBSITE_ID
      ? { host: env.VITE_UMAMI_HOST, websiteId: env.VITE_UMAMI_WEBSITE_ID }
      : undefined;
  const glitchtip = env.VITE_GLITCHTIP_DSN ? { dsn: env.VITE_GLITCHTIP_DSN } : undefined;
  if (!umami && !glitchtip) return null;
  return {
    app: 'tilly-web',
    environment: 'production',
    release: env.VITE_APP_VERSION || undefined,
    umami,
    glitchtip,
    sanitizeUrl,
  };
}

let telemetry: Telemetry | undefined;
let enabled = false;
const pending: Call[] = [];
const MAX_PENDING = 100;

function run(call: Call): void {
  if (!enabled) return;
  try {
    if (telemetry) call(telemetry);
    else if (pending.length < MAX_PENDING) pending.push(call);
  } catch {
    // telemetry must never affect the app
  }
}

/** Safe to call multiple times; only initializes once. Does not block render. */
export function initTelemetry(
  env: TelemetryEnv = import.meta.env,
  load: () => Promise<Telemetry> = () => import('ambient-telemetry/web')
): void {
  if (enabled) return;
  const config = telemetryConfig(env);
  if (!config) return;
  enabled = true;
  load()
    .then((t) => {
      t.init(config);
      telemetry = t;
      pending.splice(0).forEach(run);
    })
    .catch(() => {
      enabled = false;
      pending.length = 0;
    });
}

/** Internal user id only (never an email or name). */
export function identifyUser(userId: string, traits?: Record<string, string>): void {
  run((t) => t.identify(userId, traits));
}

export function trackProductEvent(
  name: ProductAnalyticsEventName,
  props?: Record<string, unknown>
): void {
  run((t) => t.track(name, props));
}

/** For marketing/landing events that are not part of the product event union. */
export function trackTelemetryEvent(name: string, props?: Record<string, unknown>): void {
  run((t) => t.track(name, props));
}

export function recordPage(url: string): void {
  // The library sanitizes too; doing it here as well keeps a raw path from ever being queued.
  const safe = sanitizeUrl(url);
  run((t) => t.page(safe));
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  run((t) => t.captureError(error, { extra: context }));
}

/** Call on logout so the next login doesn't inherit the previous user's identity. */
export function resetTelemetry(): void {
  run((t) => t.reset());
}

/** Test seam: forget module state. */
export function _resetTelemetryForTests(): void {
  telemetry = undefined;
  enabled = false;
  pending.length = 0;
}
